import { useDispatch, useSelector } from "react-redux";
import { useCallback, useMemo, useContext, useState, useEffect } from "react";
import {
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { setCartDetails } from "../store/modules/storeCart";
import { ToastContext } from "./context";
import {
  calculateCartItem,
  cleanObject,
  deepCopyFunction,
  findPricingViaIdHelper,
} from "../helpers";
import { DEFAULT_PRICING_ID } from "../helpers/constants";
import { useGetInfo } from "./useGetInfo";
import {
  resetCheckoutInfo,
  resetOrderPriceMod,
} from "../store/modules/checkoutInfo";
import {
  useBulkAddItemsToCart,
  useCompleteGenerateBill,
  useCreateOrderMutation,
  useGenerateBillMutation,
} from "./api/mutations/useOrder";
import { pathConstants } from "../routes/pathContants";
import { useOtpAuthTrigger } from "./useOtpAuthTrigger";
import { useGetCartItems } from "./api/queries/useOrder";
import { useQueryClient } from "react-query";

export const useCart = ({ unPendSuccessCallBack = () => {} }) => {
  const params = useParams();
  const outletContext = useOutletContext();
  const closeMobileCart = outletContext?.closeMobileCart
    ? outletContext?.closeMobileCart
    : () => {};
  const pricingType = outletContext?.pricingType || DEFAULT_PRICING_ID;
  const store_id = params?.store_id;
  const order_id = params?.order_id;
  const [search, setSearch] = useSearchParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isRefetchCart, setIsRefetchCart] = useState(false);
  const triggerToast = useContext(ToastContext);
  const { getStoreInfo } = useGetInfo();
  const storeDetails = getStoreInfo(store_id);

  const { data, isLoading: isLoadingCartItems } = useGetCartItems({
    store_id,
    isEnabled: isRefetchCart,
  });
  const updatedCartItems = data?.cartItems;

  const cartState = useSelector((state) => state?.cartReducer?.cartDetails);
  const {
    data: cartItems = [],
    total_amount,
    vat,
    ...otherCartInfo
  } = cartState || {};

  const checkoutInfo = useSelector((state) => state?.checkoutReducer);
  const { discount, delivery_fee, giftcard, loyalty_point } =
    checkoutInfo?.order_price_mod;
  const {
    clearLatestOtpAuthCode,
    getAuthCodeBaseOnKey,
    closeOtpAuthorizationPrompt,
  } = useOtpAuthTrigger({});

  const addToCartForScanedproduct = (product) => {
    const data = { quantity: 1, product };
    if (product?.variants?.length > 0) data.variant = product?.variants?.[0];

    handleAddToCart(data);
    search.delete("query");
    search.delete("is_scan");
    setSearch(search, { replace: true });
  };

  // Memoized function to update cart
  const updateCart = useCallback(
    ({
      product,
      quantity,
      variant = null,
      pricingTypeId = pricingType,
      item_numbers = null,
    }) => {
      const isService = !!product?.is_service;
      const productType = isService
        ? "store_service_properties"
        : "store_product_properties";

      const stockQuantity = variant
        ? variant?.store_variant_properties?.[0]?.stock_quantity
        : product?.[productType]?.[0]?.stock_quantity;

      const cartItemName = variant
        ? `${product?.name} -- ${variant?.option}: ${variant?.value}`
        : product?.name;

      if (
        !isService &&
        product?.composite_product_type !== "ON_DEMAND" &&
        quantity > stockQuantity
      ) {
        return triggerToast(
          `Quantity cannot exceed available stock (${stockQuantity}) for item: ${cartItemName}`,
          "warning",
        );
      }

      const newCartItems = deepCopyFunction(cartItems);
      const itemId = `${product?.id}${variant?.id || ""}`;

      const existingItemIndex = newCartItems.findIndex(
        (item) => `${item?.id}` === itemId && item?.is_service === isService,
      );
      const existingItem = newCartItems?.[existingItemIndex];

      const pricingList = variant
        ? variant?.store_variant_properties?.[0]?.pricings
        : product?.[productType]?.[0]?.pricings;

      const pricingDetails = findPricingViaIdHelper(
        existingItem?.pricing_type_id || pricingTypeId,
        pricingList,
      );

      const unitPrice = (
        pricingDetails?.price *
        (1 - pricingDetails?.discount_rate / 100)
      ).toFixed(2);

      if (existingItem) {
        if (quantity > 0) {
          newCartItems[existingItemIndex] = {
            ...existingItem,
            quantity: Number(quantity),
            total_price: existingItem?.custom_price
              ? existingItem.custom_price * Number(quantity)
              : unitPrice * Number(quantity),
            total_price_without_discount:
              pricingDetails?.price * Number(quantity),
            item_numbers,
          };
        } else {
          newCartItems.splice(existingItemIndex, 1);
        }
      } else if (quantity > 0) {
        newCartItems.unshift({
          id: itemId,
          quantity: Number(quantity),
          pricing_type_id: pricingTypeId,
          unit: {
            id: product?.unit?.id || null,
            value: 1,
            unit: product?.unit?.unit,
          },
          is_service: isService,
          cart_item_name: cartItemName,
          total_price: (unitPrice * Number(quantity)).toFixed(2),
          discount_rate: pricingDetails?.discount_rate,
          unit_price: Number(unitPrice),
          total_price_without_discount:
            pricingDetails?.price * Number(quantity),
          pricings: pricingList,
          item_numbers,
          product: {
            image_url: product?.media_files?.[0]?.url || product?.image_url,
            category: product?.category,
            id: product?.id,
            name: product?.name,
            [productType]: product?.[productType],
            is_service: isService,
            variant: variant,
            composite_product_type: product?.composite_product_type,
            unit: product?.unit,
            additional_units: product?.additional_units,
            has_custom_batch: product?.has_custom_batch,
          },
          product_unit: product?.unit,
        });
      }

      // Compute cart summary and persist data
      computeCart(newCartItems);
    },
    [cartItems, otherCartInfo],
  );

  // Function to add item to cart
  const handleAddToCart = useCallback(
    ({ product, quantity, variant, item_numbers = null }) => {
      updateCart({ product, quantity, variant, item_numbers });
    },
    [updateCart],
  );

  // Function to reduce cart item quantity
  const handleReduceCartItem = useCallback(
    ({ product, quantity, variant, item_numbers = null }) => {
      updateCart({ product, quantity, variant, item_numbers });
    },
    [updateCart],
  );

  const addProductBatchId = useCallback(
    ({ cartIndex = 0, batch_id = null, batch_name }) => {
      const newCartItems = deepCopyFunction(cartItems);

      newCartItems[cartIndex].batch_id = batch_id;
      newCartItems[cartIndex].batch_name = batch_name;
      computeCart(newCartItems);
    },
    [],
  );

  // Function to clear the entire cart
  const clearCartItem = useCallback(() => {
    const emptyCartData = {
      data: [],
      total_items: 0,
      total_amount: 0,
      total_items_amount: 0,
      vat: storeDetails?.vat,
      service_charge: storeDetails?.service_charge,
    };
    dispatch(resetOrderPriceMod());
    const cartData = calculateCartItem({
      cart_items: emptyCartData?.data,
      vat: storeDetails?.vat,
      service_charge: storeDetails?.service_charge,
      ...otherCartInfo,
      ...emptyCartData,
    });
    addCartItemToLocalStorage({
      ...cartData,
    });
  }, [dispatch, vat]);

  // Memoized function to get cart item by product ID
  const getCartItems = useCallback(
    (productId, typeKey = "product") =>
      cartItems?.find((item) => item?.[typeKey]?.id === productId),
    [cartItems],
  );

  const addCartItemToLocalStorage = (cartData) => {
    dispatch(setCartDetails(cartData));
    localStorage.setItem(
      `cartItems${store_id}`,
      JSON.stringify(cartData?.data),
    );
  };

  const computeCart = (newCartItems) => {
    const cartData = calculateCartItem({
      cart_items: newCartItems,
      vat: storeDetails?.vat,
      service_charge: storeDetails?.service_charge,
      ...otherCartInfo,
    });
    addCartItemToLocalStorage({
      ...cartData,
    });
  };

  const resetOrderOncheckOut = () => {
    clearCartItem();
    dispatch(resetCheckoutInfo());
    clearLatestOtpAuthCode([
      "add_discount_to_order",
      "add_delivery_fee_to_order",
      "add_loyalty_to_order",
      "use_giftcard_on_order",
      "use_custom_pricing_on_order",
    ]);
  };

  const getCartItemsToSubmit = () => {
    return cartItems?.map((item) => {
      return cleanObject({
        [item?.is_service ? "service_id" : "product_id"]: item?.product?.id,
        quantity: item?.quantity,
        variant_id: item?.product?.variant
          ? item?.product?.variant?.id
          : undefined,
        is_product: !item?.is_service,
        pricing_type_id: item?.pricing_type_id,
        custom_price: item?.custom_price,
        items_number: item?.item_numbers,
        unit_id: item?.unit?.id,
        batch_id: item?.batch_id,
      });
    });
  };

  const getSubmissionData = (
    params = {
      isBill: false,
      payment_provider: null,
      payment_reference: null,
    },
  ) => {
    const newCartItems = getCartItemsToSubmit();
    const isBill = params?.isBill;

    const paymentInfo = { ...checkoutInfo?.payment };

    if (paymentInfo?.pay_with_store_wallet)
      paymentInfo.pay_with_store_wallet = cleanObject({
        ...paymentInfo.pay_with_store_wallet,
        wallet_id: checkoutInfo?.store_wallet?.wallet_id,
        otp_code: checkoutInfo?.store_wallet?.otp_code,
        session_id: checkoutInfo?.store_wallet?.session_id,
      });

    if (paymentInfo?.pay_with_credit)
      paymentInfo.pay_with_credit = {
        ...paymentInfo.pay_with_credit,
        customer_id: checkoutInfo?.customer?.customer_id,
      };

    if (paymentInfo?.pay_with_pr)
      paymentInfo.pay_with_pr = {
        ...paymentInfo.pay_with_pr,
        customer_id: checkoutInfo?.customer?.customer_id,
      };

    if (paymentInfo?.pay_with_split) {
      paymentInfo.pay_with_split = cleanObject({
        ...paymentInfo.pay_with_split,
        customer_id: checkoutInfo?.customer?.customer_id,
        store_wallet_otp_code: checkoutInfo?.store_wallet?.otp_code,
        store_wallet_session_id: checkoutInfo?.store_wallet?.session_id,
        wallet_id: checkoutInfo?.store_wallet?.wallet_id,
        ledger_account_id: checkoutInfo?.ledger_account_id,
      });
    }

    if (paymentInfo?.pay_with_card)
      paymentInfo.pay_with_card = cleanObject({
        ...paymentInfo.pay_with_card,
        ledger_account_id: checkoutInfo?.ledger_account_id,
        payment_provider: params?.payment_provider,
        payment_reference: params?.payment_reference,
      });

    if (paymentInfo?.pay_with_transfer)
      paymentInfo.pay_with_transfer = cleanObject({
        ...paymentInfo.pay_with_transfer,
        ledger_account_id: checkoutInfo?.ledger_account_id,
        payment_provider: params?.payment_provider,
        payment_reference: params?.payment_reference,
      });

    const data = cleanObject(
      {
        payment: isBill
          ? { payment_method: "bill", pay_with_bill: {} }
          : paymentInfo,
        order_price_mod: Object.entries(
          checkoutInfo?.order_price_mod || {},
        ).reduce((acc, [key, value]) => {
          if (value && typeof value === "object") {
            // Only if value is a valid object

            if (key === "discount")
              acc[key] = cleanObject({
                ...value,
                discount_auth_pin: getAuthCodeBaseOnKey(
                  "add_discount_to_order",
                ),
              });

            if (key === "delivery_fee")
              acc[key] = cleanObject({
                ...value,
                delivery_fee_auth_pin: getAuthCodeBaseOnKey(
                  "add_delivery_fee_to_order",
                ),
              });

            if (key === "giftcard")
              acc[key] = cleanObject({
                ...value,
                giftcard_auth_pin: getAuthCodeBaseOnKey(
                  "use_giftcard_on_order",
                ),
              });

            if (key === "loyalty_point")
              acc[key] = cleanObject({
                ...value,
                loyalty_auth_pin: getAuthCodeBaseOnKey("add_loyalty_to_order"),
              });
          }

          return acc;
        }, {}),
        add_bulk_items_to_cart: cleanObject({
          store_id,
          items: newCartItems,
          custom_pricing_auth_pin: getAuthCodeBaseOnKey(
            "use_custom_pricing_on_order",
          ),
        }),
        order_details: cleanObject({
          customer_id: checkoutInfo?.customer?.customer_id,
          send_to_kitchen: true,
          order_table_id: checkoutInfo?.table?.order_table_id,
          diners: checkoutInfo?.table?.diners,
          order_notes: checkoutInfo?.order_note,
          order_type: checkoutInfo?.order_type,
        }),
      },
      ["customer", "order_note", "order_type"],
    );

    return data;
  };

  const { mutate: createOrder, isLoading: isCreatingOrder } =
    useCreateOrderMutation();
  const { mutate: generateBill, isLoading: isGeneratingBill } =
    useGenerateBillMutation();
  const { mutate: completeGenerateBill, isLoading: isCompletingBill } =
    useCompleteGenerateBill();

  const submitOrder = (
    params = { isBill: false, payment_provider: null, payment_reference: null },
  ) => {
    const isBill = params?.isBill;
    let data = getSubmissionData(
      cleanObject({
        isBill: isBill,
        payment_provider: params?.payment_provider,
        payment_reference: params?.payment_reference,
      }),
    );
    const isBillCompletion = order_id ? true : false;
    const triggerCall = isBillCompletion
      ? completeGenerateBill
      : isBill
        ? generateBill
        : createOrder;

    if (isBillCompletion)
      data = cleanObject({ ...data, order_id, store_id }, [
        "add_bulk_items_to_cart",
        "order_price_mod",
      ]);

    triggerCall(data, {
      onSuccess: async (res) => {
        closeOtpAuthorizationPrompt();
        resetOrderOncheckOut();

        if (res?.data?.data)
          await queryClient.setQueryData(
            ["orderDetails", `${res?.data?.data?.id}`],
            {
              ...res?.data?.data,
            },
          );

        queryClient.invalidateQueries(["getProductList"]);
        queryClient.invalidateQueries(["getOrderList"]);
        closeMobileCart();

        navigate(
          pathConstants.SELL_MODE_ORDER_EXTRA({
            store_id: store_id,
            order_id: res?.data?.data?.id,
          }),
        );
      },
    });
  };

  const { mutate: bulkAddToCart, isLoading: isAddingToCart } =
    useBulkAddItemsToCart();
  const handleBulkAddToCart = ({ onSuccess = () => {} }) => {
    const newCartItems = getCartItemsToSubmit();
    const data = cleanObject({
      store_id,
      items: newCartItems,
      custom_pricing_auth_pin: getAuthCodeBaseOnKey(
        "use_custom_pricing_on_order",
      ),
    });

    bulkAddToCart(data, {
      onSuccess: () => onSuccess(),
    });
  };

  const handleGenerateBill = () => {
    submitOrder({ isBill: true });
  };

  useEffect(() => {
    if (updatedCartItems?.length > 0) {
      resetCheckoutInfo();
      computeCart(updatedCartItems);
      setIsRefetchCart(false);
      unPendSuccessCallBack();
    }
  }, [updatedCartItems]);

  return useMemo(
    () => ({
      cartItems,
      total_amount,
      vat,
      discount,
      delivery_fee,
      giftcard,
      loyalty_point,
      totalCalculatedPrice: Number(
        (
          total_amount -
          (discount ? Number(discount?.amount) : 0) +
          (delivery_fee ? Number(delivery_fee?.amount) : 0) -
          (loyalty_point ? Number(loyalty_point?.amount) : 0) -
          (giftcard ? Number(giftcard?.amount) : 0)
        ).toFixed(2),
      ),
      isAddingToCart,
      storeDetails,
      store_id,
      isCreatingOrder: isCreatingOrder || isCompletingBill,
      isLoadingCartItems,
      isGeneratingBill,
      checkoutInfo,
      addProductBatchId,
      handleGenerateBill,
      handleBulkAddToCart,
      setIsRefetchCart,
      resetOrderOncheckOut,
      addToCartForScanedproduct,
      submitOrder,
      addCartItemToLocalStorage,
      clearCartItem,
      handleAddToCart,
      handleReduceCartItem,
      getCartItems,
      ...otherCartInfo,
    }),
    [
      cartItems,
      total_amount,
      vat,
      otherCartInfo,
      discount,
      delivery_fee,
      giftcard,
      loyalty_point,
      isCreatingOrder,
      isCompletingBill,
      storeDetails,
      isLoadingCartItems,
      isAddingToCart,
      isGeneratingBill,
      checkoutInfo,
      clearCartItem,
      handleAddToCart,
      handleReduceCartItem,
      getCartItems,
    ],
  );
};
